/*
  Custom element: <pdp-recommendations>
  Host for the product page's "Goes well with" row (sections/pdp-recommendations.liquid).

  On the page render the section emits this element EMPTY, carrying `data-url`: Shopify's
  Product Recommendations endpoint for the same section
  (/recommendations/products?section_id=…&product_id=…&limit=10&intent=complementary).
  When the host comes within ~400px of the viewport it fetches that URL, which is the same
  section rendered again by Shopify with `recommendations.products` filled, and moves the
  fetched host's children — waves, heading, carousel — into itself. Every house rule
  (sold out, packs, the buy-box card's product, already in the cart) is applied in Liquid
  during that second render, so this script decides nothing about products.

  An empty answer — nothing set in Search & Discovery for this product, or nothing that
  passes the rules — leaves the host empty: no heading, no band, no fallback. That is the
  contract of the picked design (The Quiet Cart, candidate 4), not a failure state.

  Moving the fetched nodes into the live document is what upgrades the custom elements
  inside them (<infinite-carousel>, <best-seller-card>) with their final children present.
  Reviews.io's rating stars are hydrated once at DOMContentLoaded by
  initRukRatingSnippet() in layout/theme.liquid; cards that arrive later need that call
  again, exactly as the theme already repeats it on shopify:section:load.

  Dawn's own <product-recommendations> (global.js) is not reused: its empty-answer branch
  removes the element only when it carries Dawn's `complementary-products` class and finds
  no <slideshow-component>, which would delete this row's carousel.

  Define guarded: a section-level script runs once per mounted instance.
*/
if (!customElements.get('pdp-recommendations')) {
  customElements.define(
    'pdp-recommendations',
    class PdpRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.hasAttribute('data-loaded') || !this.dataset.url) return;

        this._observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            this.#load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this._observer.observe(this);
      }

      disconnectedCallback() {
        this._observer?.disconnect();
      }

      async #load() {
        try {
          const res = await fetch(this.dataset.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fetched = doc.querySelector('pdp-recommendations');
          if (!fetched || fetched.children.length === 0) {
            this.#empty();
            return;
          }

          this.replaceChildren(...fetched.childNodes);
          this.setAttribute('data-loaded', '');

          if (typeof window.initRukRatingSnippet === 'function') window.initRukRatingSnippet();
        } catch (err) {
          console.error('[pdp-recommendations]', err);
          this.#empty();
        }
      }

      #empty() {
        this.replaceChildren();
        this.setAttribute('data-empty', '');
      }
    }
  );
}
